import React from 'react';
import {
    Box,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {
    CardGiftcard as CardGiftcardIcon,
    DeleteOutline as DeleteOutlineIcon,
    EditNote as EditNoteIcon,
    PersonOffOutlined as PersonOffIcon
} from '@mui/icons-material';
import { CartSummary } from '@api/ShoppingCart/ShoppingCart';
import { currencyFormatter } from '@utils/Numbers';

// Quién es el cliente y qué puntos usa — más la nota del pedido.
//
// Layout pedido por Irving (2026-08-27): **cliente y puntos en UN solo renglón**,
// la nota debajo, y el canal de venta se fue a la fila de los botones de cobro
// (`OrderChannelSelect`). El chip de arriba solo indica que hay cliente/nota; el
// valor y su bote de basura viven aquí.
interface OrderCustomerFormProps {
    shoppingCart?: CartSummary;
    onCustomerNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    // Quita al cliente del pedido. ⚠️ El POS DEBE resetear también los puntos:
    // el canje era de ESE cliente.
    onRemoveCustomer: () => void;
    rewardAmount: number;
    setRewardAmount: (amount: number) => void;
    getPointsList: (points: number) => any[];
    onEditNote: () => void;
    onRemoveNote: () => void;
}

export const OrderCustomerForm: React.FC<OrderCustomerFormProps> = ({
    shoppingCart,
    onCustomerNameChange,
    onRemoveCustomer,
    rewardAmount,
    setRewardAmount,
    getPointsList,
    onEditNote,
    onRemoveNote
}) => {
    const customer = shoppingCart?.customer;
    const currentPoints = customer?.profile?.wallet?.currentPoints || 0;
    // Solo se ofrece quitar cuando hay ALGUIEN a quién quitar: un cliente
    // registrado (uid) o un nombre escrito. El walk-in en blanco que deja el
    // propio "quitar cliente" —y el placeholder '0000'— no cuentan, o el botón
    // aparecería siempre sin nada que hacer.
    const customerName = (customer?.profile?.name || '').trim();
    const canRemoveCustomer = !!customer?.uid || (!!customerName && customerName !== '0000');

    return (
        <>
            {customer && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    <TextField
                        // Se conserva el autoFocus: en un walk-in el cajero teclea
                        // el nombre en cuanto aparece el campo
                        autoFocus
                        variant="outlined"
                        disabled={!!customer.uid}
                        value={customer.profile?.name || ''}
                        onChange={onCustomerNameChange}
                        placeholder="Nombre del cliente"
                        sx={{ flex: 1, minWidth: 140 }}
                    />
                    {/* Quitar al cliente: se eligió al equivocado o el pedido pasa a
                        ser de mostrador. Deja el campo listo para teclear un nombre.
                        Pide confirmación (arrastra los puntos consigo). */}
                    {canRemoveCustomer && (
                        <IconButton
                            aria-label="Quitar cliente"
                            onClick={onRemoveCustomer}
                            sx={{ border: 1, borderColor: 'divider', p: 1, flexShrink: 0 }}
                        >
                            <PersonOffIcon />
                        </IconButton>
                    )}

                    {currentPoints > 0 && (
                        <FormControl sx={{ flex: 1, minWidth: 130 }}>
                            <InputLabel id="reward-select-label">Puntos</InputLabel>
                            <Select
                                disabled={!!shoppingCart?.promotionalCode}
                                labelId="reward-select-label"
                                id="reward-select"
                                value={rewardAmount}
                                label="Puntos"
                                onChange={(e) => setRewardAmount(Number(e.target.value))}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <CardGiftcardIcon sx={{ color: '#895BF1' }} />
                                    </InputAdornment>
                                }
                                sx={{ borderRadius: 4, fontWeight: 600 }}
                            >
                                {getPointsList(currentPoints).map((list, index) => (
                                    <MenuItem key={index} value={list.value}>
                                        <Stack direction="row" justifyContent="space-between" spacing={4}>
                                            <Typography>{`${list.points} puntos`}</Typography>
                                            <Typography>{currencyFormatter(list.value)}</Typography>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Stack>
            )}

            {/* La NOTA del pedido: el chip de arriba solo dice que hay nota; el
                texto se lee aquí y se quita desde aquí. La card amarilla que
                ocupaba tres bloques de alto NO vuelve. */}
            {shoppingCart?.note && (
                <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={0.5}
                    onClick={onEditNote}
                    sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: '#FFA7001A',
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer'
                    }}
                >
                    <EditNoteIcon fontSize="small" sx={{ mt: 0.25 }} />
                    <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={600}>
                            {shoppingCart.note}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        aria-label="Quitar nota"
                        onClick={(event) => {
                            event.stopPropagation();
                            onRemoveNote();
                        }}
                        sx={{ p: 0.25 }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Stack>
            )}
        </>
    );
};
